import { clamp } from "lodash-es";
import type { StateCreator } from "zustand";

import { handleZoom } from "../../main-canvas/user-events/user-events-utils.ts";
import { type components, TrackEnum } from "../../openapi";
import type { Range, SelectionRange, Track, ZoomRange } from "../../types.ts";
import type { GenomeViewerStore } from "../store.ts";

export interface RangeSelectionState {
  selectedRange?: SelectionRange;
  zoomRange?: ZoomRange;
  leftSelectionDragging: boolean;
  rightSelectionDragging: boolean;
  leftSelectionHovering: boolean;
  rightSelectionHovering: boolean;
  trackGroupsInSelection: string[];
  fullRangeSelection: boolean;
}

export interface RangePerContig extends Range {
  contigId: number;
  /** Hex color strings */
  trackColors?: components["schemas"]["TrackColors"];
}

export interface RangeSelectionActions {
  zoomToRange: () => void;
  getSelectedRange: () => RangePerContig[] | null;
  toggleFullRangeSelection: () => void;
}

export type RangeSelectionSlice = RangeSelectionState & RangeSelectionActions;

export const initialRangeSelectionState = {
  selectedRange: undefined,
  zoomRange: undefined,
  leftSelectionDragging: false,
  rightSelectionDragging: false,
  leftSelectionHovering: false,
  rightSelectionHovering: false,
  trackGroupsInSelection: [],
  fullRangeSelection: true, // select all by default
};

export const createRangeSelectionSlice: StateCreator<GenomeViewerStore, [], [], RangeSelectionSlice> = (set, get) => ({
  ...initialRangeSelectionState,
  zoomToRange() {
    const region = get().zoomRange;
    const canvas = get().canvas;
    if (!region || !canvas) return;

    const minX = Math.min(region.startX, region.stopX);
    const maxX = Math.max(region.startX, region.stopX);

    const zoomFactor = (maxX - minX) / canvas.width;

    handleZoom(zoomFactor, minX, true);

    // Clear the rectangle
    set({
      zoomRange: undefined,
    });
  },

  getSelectedRange() {
    let region: Range | undefined = get().selectedRange;
    // In the case where no region is selected, try to send the full visible region on screen
    const sendFullVisibleRegion = !region;

    if (!region) {
      region = {
        start: Math.floor(get().scrollX * get().basePerPixel),
        stop: Math.ceil((get().scrollX + get().canvas!.width) * get().basePerPixel),
      };
    }

    const start = Math.min(region.start, region.stop);
    const stop = Math.max(region.start, region.stop);

    const selection: RangePerContig[] = [];

    [...get().trackGroups.entries()]
      .filter(
        ([groupId, trackGroup]) =>
          trackGroup.graphics.isVisible && (sendFullVisibleRegion || get().trackGroupsInSelection.includes(groupId)),
      )
      .forEach(([groupId, trackGroup]) => {
        const startRange = clamp(start - trackGroup.graphics.baseShift, 0, trackGroup.graphics.length) + 1; // +1 because they are 1-based indexing
        const stopRange = clamp(stop - trackGroup.graphics.baseShift, 1, trackGroup.graphics.length);
        const activeTracks = [...get().tracks.values()].filter((track) => track.groupId === groupId);

        if (startRange <= stopRange) {
          const geneTrack = activeTracks.find((track) => track.trackType === TrackEnum.GENE);
          const proteinTrack = activeTracks.find((track) => track.trackType === TrackEnum.PROTEIN_LOCATION);
          const nuFeatureTracks = activeTracks.filter((track) => track.trackType === TrackEnum.NUCLEOTIDE_FEATURE);

          selection.push({
            contigId: trackGroup.contigId,
            start: startRange,
            stop: stopRange,
            trackColors: {
              gene: geneTrack?.color,
              protein: proteinTrack?.color,
              nuFeatures: [
                ...nuFeatureTracks.map((track: Track) => ({
                  featureTypeId: track.subType!.id,
                  color: track.color,
                })),
              ],
            },
          });
        }
      });

    return selection.length > 0 ? selection : null;
  },
  toggleFullRangeSelection() {
    const newFullRangeSelection = !get().fullRangeSelection;
    set({
      fullRangeSelection: newFullRangeSelection,
      trackGroupsInSelection: newFullRangeSelection ? [...get().trackGroups.keys()] : get().trackGroupsInSelection,
    });
  },
});
